import { router } from '@/app/router';
import { paths } from '@/config/paths';
import { openAssessment } from '@/features/assessments';
import type { Cta } from '@/features/hero/config/hero-states-v2';
import type { HeroData } from '@/features/hero/hooks';
import type { HeroStateCallbacks } from '@/features/hero/utils/hero-state-parser';
import { AssessmentType } from '@/lib/analytics/types';

/** Shared prop builders for the online hero states. */

export const makeCta = (
  heroData: HeroData,
  callbacks?: HeroStateCallbacks
): Cta => {
  if (heroData.lastSession?.attendance?.status === 'MISSED') {
    return {
      variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
      label: 'Watch Recording',
      onClick: () => {
        callbacks?.openRecordingDrawer?.();
      },
    };
  }
  const assessment = heroData?.latestIncompleteAssessment;
  const assessmentId = assessment?.assessmentId;
  if (assessmentId && assessment) {
    return {
      variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
      label: 'Start Practice',
      onClick: () => {
        // Track the event before opening assessment
        if (callbacks?.trackEvent) {
          const assessmentType = assessment.assessmentCategoryType;

          callbacks.trackEvent('attemptAssessment', 1, {
            source: 'heroComponent',
            type: assessmentType.toLowerCase() as AssessmentType,
            reattempt: assessment.completionStatus ?? false,
            assessmentName: assessment.assessmentName ?? '',
          });
        }

        openAssessment(assessmentId);
      },
    };
  }
  return {
    variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
    label: 'Start Practice',
    onClick: () => void router.navigate(paths.app.brainGym.getHref()),
  };
};

export const makeStats = (): string => {
  return 'Keep up the consistency, your hard work pays off.';
};

export const makeSubtitle = (heroData: HeroData): string => {
  if (heroData.latestIncompleteAssessment) {
    return `Let's lock in your learning — your next quiz is ready`;
  }
  return `You've completed all quizzes, now test yourself with fun practice questions!`;
};

export const includeName = (heroData: HeroData): string => {
  if (heroData.studentName) {
    return ` ${heroData.studentName}`;
  }
  return '';
};

export const isOnTrial = (heroData: HeroData): boolean =>
  heroData.rawData.selectedCourse?.is_trial ?? false;

/** In seconds. Same threshold as the session card's Join button. */
export const SESSION_LIVE_THRESHOLD = 5 * 60;
